import Image from 'next/image';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { titleCase } from 'title-case';

import { Header } from '../../components/Header';
import { PageMetadata } from '../../components/PageMetadata';
import { Footer } from '../../components/Footer/Footer';
import { Section, Row, Column } from '../../components/Section';
import { Text } from '../../components/Text';
import { CallToActionSection } from '../../components/CallToActionSection';

import { GetServerPageProps } from '../../lib/apollo';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import {
  getServerPageArticle,
  getServerPageArticleSlugs,
} from './article.generated';
import { RichText } from '../../lib/copyRendering';
import { DateTime } from 'luxon';
import { ArticleCard } from '../../components/ArticleCard';
import { DateTimeDisplay } from '../../components/DateTimeDisplay';
import articleAuthor from '../../assets/article-author.svg';
import { Fragment } from 'react';

export default function Article({
  pageData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const article = pageData.articleCollection?.items[0];

  if (!article) {
    throw new Error('Unable to load article');
  }

  const metaDescription = article.description
    ? documentToPlainTextString(article.description.json)
    : documentToPlainTextString(article.content?.json, '\n')
        .split('\n')
        .slice(0, 3)
        .join('\n');

  const relatedArticles =
    article.relatedCollection?.items.flatMap((item) =>
      item?.__typename === 'Article' ? item : [],
    ) ?? [];

  return (
    <>
      <PageMetadata
        pageTitle={article.seoTitleOverride ?? article.title ?? undefined}
        description={article.seoDescriptionOverride ?? metaDescription}
        openGraphImage={article.image?.url ?? undefined}
      />
      <Header />
      <Section className="!pt-0 lg:pb-0" borderless>
        {article.image?.url && (
          <div className="relative h-64 sm:h-96 md:h-[28em] lg:h-[32em] xl:h-[35em] 2xl:h-[45em]">
            <Image
              src={article.image.url}
              fill
              sizes="100vw"
              alt={article.image.description ?? ''}
              className="object-cover mx-auto"
              priority
            />
          </div>
        )}
        <Row inset>
          <Column className="relative lg:-top-60 lg:!-mb-60 lg:z-10 lg:bg-gradient-to-b from-white/80 via-white to-white lg:!px-8 lg:!py-6 rounded-2xl lg:drop-shadow">
            <div className="text-sm text-bright-purple truncate">
              {article.categoriesCollection?.items.map(
                (item, index, collection) => (
                  <Fragment key={item?.slug}>
                    <a key={item?.slug} href={`/articles/${item?.slug}`}>
                      {item?.name}
                    </a>
                    {index < collection.length - 1 ? ', ' : null}
                  </Fragment>
                ),
              )}
            </div>
            <Text.H1>{titleCase(article.title ?? '')}</Text.H1>
            <div className="max-w-prose text-gray-500">
              <RichText content={article.description} />
            </div>
            <div className="flex items-center">
              <Image
                src={articleAuthor}
                alt="HealthPlans of NC"
                className="mr-3"
              />
              <div>
                <p className="text-sm font-medium">{article.author}</p>
                <p className="text-sm text-gray-500">
                  {article.publishedDate && (
                    <DateTimeDisplay
                      dateString={article.publishedDate}
                      format={DateTime.DATE_MED}
                    />
                  )}
                </p>
              </div>
            </div>
          </Column>
        </Row>
      </Section>
      <Section borderless>
        <Row>
          <Column>
            <div className="max-w-prose mx-auto">
              <RichText content={article.content} />
            </div>
          </Column>
        </Row>
      </Section>
      <CallToActionSection quoteAction />
      {relatedArticles.length > 0 && (
        <>
          <Section>
            <Row>
              <Column>
                <Text.H2>Related Articles</Text.H2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                  {relatedArticles.map((article) => (
                    <ArticleCard article={article} key={article.sys.id} />
                  ))}
                </div>
              </Column>
            </Row>
          </Section>
          <CallToActionSection quoteAction />
        </>
      )}
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps<
  { pageData: GetServerPageProps<typeof getServerPageArticle>['data'] },
  { slug: string }
> = async ({ params }) => {
  const slug = params?.slug;
  return {
    props: {
      pageData: (
        await getServerPageArticle({
          variables: { slug: Array.isArray(slug) ? slug[0] : slug },
        })
      ).props.data,
    },
  };
};

export async function getStaticPaths() {
  const { props } = await getServerPageArticleSlugs({});
  return {
    paths: props.data.articleCollection?.items.map((article) => ({
      params: {
        slug: article?.slug,
      },
    })),
    fallback: false,
  };
}
